import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { UserService } from '../user/user.service';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { LiveChatService } from '../live-chat/live-chat.service';
import { HttpErrorResponse } from '@angular/common/http';
import { RegistrationFlowService } from '../registration-flow/registration-flow.service';
import { LoginNavigationService } from '../login-navigation/login-navigation.service';

@Injectable({
  providedIn: 'root'
})
export class UserMeResolverService {

  constructor(
    private service: UserService,
    private router: Router,
    public liveChatService: LiveChatService,
    private registrationFlowService: RegistrationFlowService,
    private loginNavigationService: LoginNavigationService,
  ) { }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> | Promise<any> | any {

    const checkRegistrationProgress = this.loginNavigationService.checkRegistrationProgressOnNextNavigation;
    this.loginNavigationService.checkRegistrationProgressOnNextNavigation = false;

    return this.service.getMe(!!route.data.skipUserMeAuth)
      .pipe(
        tap(user => {
          // Segmentio Identify call required here instead of calling just once, because they want to track
          // user details updates.
          this.service.identify(user);

          // If the route data contains the property 'checkDetailsProvided' and the user data resolved
          // doesn't contain any address and/or phone numbers, a redirection to the view to provide those
          // information will happen.
          // This is mainly to prevent that a user start the pet claiming process without having provided
          // those information.
          if (route.data.checkDetailsProvided && !this.service.areDetailsProvidedFor(user)) {
            this.router.navigate(['/account/owner-details']);
            return;
          }

          // prevent access to route unless the user has the required productType
          if (route.data.requiresProductType && user?.productType !== route.data.requiresProductType) {
            this.router.navigate(['/dashboard']);
          }
        }),
        // After login, send a user with an incomplete registration to the step they are on. Waits for
        // the steps so the requested page doesn't render before the redirect. Reuses the steps the route's
        // steps resolver loads in parallel, since the cache is cleared on logout.
        // Skipped when the user was just sent to provide their details, so that redirect wins.
        switchMap(user => {
          if (!checkRegistrationProgress || (route.data.checkDetailsProvided && !this.service.areDetailsProvidedFor(user))) {
            return of(user);
          }
          return this.registrationFlowService.getResumeRoute(user, true).pipe(
            tap(resumeRoute => resumeRoute && this.router.navigate([resumeRoute])),
            map(() => user)
          );
        }),
        catchError(err => {
          if (err instanceof HttpErrorResponse && err.status === 401 && route.data.skipUserMeAuth) {
            return of(null);
          }
          return throwError(err);
        })
      );
  }
}
